import { useAuth0 } from '@auth0/auth0-react';
import { Checkbox, Field, Label, Typography } from '@remarkable/ark-web';
import { useForm } from 'react-hook-form';

import { useEnterpriseEnrollmentStatus } from 'src/api/queries/enterpriseEnrollment';
import {
  useAccountLinkingListener,
  useAuth0Claims,
  useLoginToLinkableAccount,
} from 'src/api/queries/user';
import {
  Button,
  Divider,
  Feedback,
  Form,
  NotificationBox,
  Sheet,
  SomethingWentWrong,
  Spinner,
} from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { TestId } from 'src/testids';
import { URLS } from 'src/utils/urls/urls';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { CancelSetupModal } from '../components/CancelSetup';
import { EnterpriseSteps } from '../components/EnterpriseSteps';
import { useHasLinkedAccounts } from '../utils/useHasLinkedAccounts';

interface FormState {
  hasVerifiedEmailInIdP: boolean;
}

export function VerifySamlPage() {
  const accountLinkingListener = useAccountLinkingListener({
    returnUrl: URLS.ENTERPRISE_CONFIRM_SETUP,
    componentLocation: ComponentLocations.ENTERPRISE.ENROLL_VERIFY_SAML,
  });
  const loginToLinkableAccount = useLoginToLinkableAccount();
  const enrollmentStatus = useEnterpriseEnrollmentStatus();
  const linkedStatus = useHasLinkedAccounts();
  const form = useForm<FormState>();
  const { user } = useAuth0();
  const claims = useAuth0Claims();

  const isLoggedInToSSO = !!claims.data?.org_id;

  if (accountLinkingListener.isPending || linkedStatus.isPending) {
    return <Spinner />;
  }

  if (linkedStatus.isError) {
    return <SomethingWentWrong error={linkedStatus.error} />;
  }

  return (
    <AppPage.Container>
      <AppPage.Header
        title="Setting up Enterprise SSO"
        className="max-w-l2xl text-center"
      />
      <AppPage.Content
        className="max-w-ll"
        data-cy={TestId.PageEnterpriseEnrollmentVerifySAML}
      >
        <EnterpriseSteps />

        <Sheet className="gap-24">
          <div className="flex items-center justify-between gap-16">
            <Typography as="h2" variant="heading-sm">
              Verify the SAML settings
            </Typography>
          </div>

          <Form
            {...form}
            className="flex flex-col gap-24"
            onSubmit={() => {
              if (typeof enrollmentStatus.data?.connectionId !== 'string')
                return;

              if (!linkedStatus.isSuccess) return;

              loginToLinkableAccount.mutate({
                connection: enrollmentStatus.data.connectionId,
                startLinking: !linkedStatus.data.isLinked,
              });
            }}
          >
            {isLoggedInToSSO ? (
              <>
                <Typography variant="body-md">
                  Your SAML setup has been verified.
                </Typography>

                <Divider />
              </>
            ) : (
              <div className="flex flex-col gap-24">
                <Typography variant="body-md">
                  To confirm that everything is working correctly, add your
                  email address to your identity provider and verify by logging
                  in with SSO. If everything&apos;s set up as it should,
                  you&apos;ll be able to log right in.
                </Typography>

                <Divider />

                <Field variant="horizontal">
                  <Checkbox
                    required
                    id="verify-saml-checkbox"
                    {...form.register('hasVerifiedEmailInIdP', {
                      required: true,
                    })}
                    data-cy={TestId.CheckboxEnterpriseVerifySAML}
                  />
                  <Label htmlFor="verify-saml-checkbox">
                    I&apos;ve added my email address (
                    <b>{user?.email ?? 'email not found'}</b>) to my identity
                    provider.
                  </Label>
                </Field>

                <div className="w-full bg-neutral-light-3 px-12 py-8">
                  <Typography variant="body-sm">
                    By verifying, you agree that:
                  </Typography>

                  <ul className="list-disc pl-24">
                    <li>
                      <Typography variant="body-sm">
                        Control of your reMarkable account and content will be
                        transferred to your employer.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body-sm">
                        The Terms and Conditions for reMarkable Accounts
                        you&apos;ve accepted, including any active
                        subscriptions, will end.
                      </Typography>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <div className="flex w-full flex-col-reverse flex-wrap-reverse gap-24 lm:flex-row">
              <div className="flex flex-1 ls:justify-center lm:justify-start">
                <CancelSetupModal
                  componentLocation={
                    ComponentLocations.ENTERPRISE.ENROLL_VERIFY_SAML
                  }
                  trigger={
                    <Button
                      variant="tertiary-neutral"
                      className="w-full ls:w-fit"
                    >
                      Cancel
                    </Button>
                  }
                />
              </div>

              <div className="flex flex-col-reverse flex-wrap-reverse gap-24 ls:flex-row">
                <Button
                  as="a"
                  variant="secondary"
                  to={URLS.ENTERPRISE_SETUP_SAML}
                  className="w-full ls:w-fit ls:flex-1 lm:flex-initial"
                  analytics={{
                    location: ComponentLocations.ENTERPRISE.ENROLL_VERIFY_SAML,
                    text: 'Previous',
                    action: 'previous',
                  }}
                >
                  Previous
                </Button>
                {isLoggedInToSSO ? (
                  <Button
                    as="a"
                    to={URLS.ENTERPRISE_CONFIRM_SETUP}
                    variant="primary"
                    className="w-full ls:w-fit ls:flex-1 lm:flex-initial"
                    analytics={{
                      location:
                        ComponentLocations.ENTERPRISE.ENROLL_VERIFY_SAML,
                      text: 'Continue',
                      action: 'continue',
                    }}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    type="submit"
                    className="w-full ls:w-fit ls:flex-1 lm:flex-initial"
                    disabled={
                      loginToLinkableAccount.isPending || linkedStatus.isPending
                    }
                    loading={
                      loginToLinkableAccount.isPending || linkedStatus.isPending
                    }
                    analytics={{
                      location:
                        ComponentLocations.ENTERPRISE.ENROLL_VERIFY_SAML,
                      text: linkedStatus.data?.isLinked
                        ? 'Login to SSO'
                        : 'Verify',
                      action: linkedStatus.data?.isLinked
                        ? 'verify saml'
                        : 'login to sso',
                    }}
                  >
                    {linkedStatus.data.isLinked ? 'Login to SSO' : 'Verify'}
                  </Button>
                )}
              </div>
            </div>
          </Form>

          <NotificationBox error={loginToLinkableAccount.error} />
        </Sheet>
        <Feedback
          className="mt-48"
          feedbackTargetId="enterprise-enroll-saml-verify"
          title="Did you find what you were looking for?"
        />
      </AppPage.Content>
    </AppPage.Container>
  );
}
